// Éditeur WYSIWYG minimaliste (Tiptap) — barre d'outils volontairement réduite
// pour rester alignée sur l'allowlist de sanitisation backend :
// gras / italique / souligné / lien / listes / H3 / citation
import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading3, Quote, Link as LinkIcon, Link2Off } from "lucide-react";

const BoutonOutil = ({ actif, onClick, label, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    data-testid={testid}
    className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${
      actif ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder = "Écrivez votre message…" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Réduction volontaire : seulement les nodes/marks qu'on autorise côté backend
        heading: { levels: [3] },
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose-inyange focus:outline-none min-h-[180px] px-4 py-3",
        "data-testid": "message-editor-content",
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Synchronise le contenu si `value` change depuis l'extérieur (ex : réinitialisation)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) editor.commands.setContent(value || "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const definirLien = () => {
    const precedent = editor.getAttributes("link").href;
    const url = window.prompt("URL du lien (http/https/mailto)", precedent || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="rounded-xl border border-border bg-background overflow-hidden focus-within:ring-2 focus-within:ring-accent">
      <div className="flex items-center gap-1 border-b border-border bg-muted/40 px-2 py-1.5">
        <BoutonOutil actif={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Gras" testid="editor-btn-bold">
          <Bold className="h-4 w-4" />
        </BoutonOutil>
        <BoutonOutil actif={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italique" testid="editor-btn-italic">
          <Italic className="h-4 w-4" />
        </BoutonOutil>
        <BoutonOutil actif={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} label="Souligné" testid="editor-btn-underline">
          <UnderlineIcon className="h-4 w-4" />
        </BoutonOutil>
        <div className="w-px h-5 bg-border mx-1" />
        <BoutonOutil actif={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} label="Titre H3" testid="editor-btn-h3">
          <Heading3 className="h-4 w-4" />
        </BoutonOutil>
        <BoutonOutil actif={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Liste à puces" testid="editor-btn-bullet">
          <List className="h-4 w-4" />
        </BoutonOutil>
        <BoutonOutil actif={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Liste numérotée" testid="editor-btn-ordered">
          <ListOrdered className="h-4 w-4" />
        </BoutonOutil>
        <BoutonOutil actif={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} label="Citation" testid="editor-btn-quote">
          <Quote className="h-4 w-4" />
        </BoutonOutil>
        <div className="w-px h-5 bg-border mx-1" />
        <BoutonOutil actif={editor.isActive("link")} onClick={definirLien} label="Insérer / modifier un lien" testid="editor-btn-link">
          <LinkIcon className="h-4 w-4" />
        </BoutonOutil>
        {editor.isActive("link") && (
          <BoutonOutil actif={false} onClick={() => editor.chain().focus().unsetLink().run()} label="Retirer le lien" testid="editor-btn-unlink">
            <Link2Off className="h-4 w-4" />
          </BoutonOutil>
        )}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
